/**
 * /scan-effect — the page: pick → preview → settings → file.
 *
 * The preview and the file come from the same engine and the same seed: what
 * is shown at screen size is what the export draws at the chosen dpi, page
 * by page, each page with its own tilt and grain (engine.js says why).
 */
import { PRESETS, pageDraw, scanPage } from './engine.js';

const CFG = window.SE_CFG || {};
const SLUG = CFG.slug || 'scan-effect';
const T = (k) => (CFG.i18n && CFG.i18n[k] != null ? CFG.i18n[k] : k);
const $ = (id) => document.getElementById(id);
const toast = (m, kind) => { try { window.__toast ? window.__toast(m, kind) : console.log(m); } catch (_) {} };
const stage = (n, d) => { try { window.__stage && window.__stage(n, d); } catch (_) {} };
const funnel = (n, d) => { try { window.__aeFunnel && window.__aeFunnel(n, d); } catch (_) {} };
const diag = (d) => { try { window.__aeDiagnostic && window.__aeDiagnostic(d); } catch (_) {} };
const complete = (d) => { try { window.__aeToolComplete && window.__aeToolComplete(d); } catch (_) {} };
const ST = window.__toolSettings;
const pref = (k, d) => { try { return ST ? ST.load(SLUG, k, d) : d; } catch (_) { return d; } };
const setPref = (k, v) => { try { ST && ST.save(SLUG, k, v); } catch (_) {} };

/** A page cap, not a size cap: past it this is a batch job, not a document. */
const MAX_PAGES = 200;
const PREVIEW_EDGE = 1100;              // px on the long side of the preview
const JPEG_Q = 0.82;                    // a scanner's JPEG, not a photographer's
/** Slider 0..100 ↔ the engine's physical units. */
const RANGE = { tilt: 3, noise: 1.2, blur: 1.2, paper: 1, shadow: 1, dust: 1 };
const FINE = Object.keys(RANGE);

const S = {
    files: [],              // the picked File objects (for the cap key and the name)
    pages: [],              // { kind:'pdf', doc, n, wPt, hPt } | { kind:'img', blob, wPx, hPx, wPt, hPt }
    cur: 0,
    preset: pref('preset', 'office'),
    opts: null,
    seed: (Math.random() * 4294967296) >>> 0,
    dpi: +pref('dpi', 200) || 200,
    fmt: 'pdf',
    compare: false,
    clean: new Map(),       // page index → clean preview canvas
    busy: false,
    gen: 0,
};

/* ─── options ─────────────────────────────────────────────────────────────── */

function optsFromPreset(name) {
    const p = PRESETS[name] || PRESETS.office;
    return Object.assign({ samePerPage: false }, p);
}

function syncControls() {
    Array.from(document.querySelectorAll('input[name="sePreset"]')).forEach((r) => { r.checked = r.value === S.preset; });
    Array.from($('seMode').children).forEach((b) => b.classList.toggle('is-on', b.dataset.v === S.opts.mode));
    Array.from($('seDpi').children).forEach((b) => b.classList.toggle('is-on', +b.dataset.v === S.dpi));
    Array.from($('seFmt').children).forEach((b) => b.classList.toggle('is-on', b.dataset.v === S.fmt));
    FINE.forEach((k) => {
        const v = Math.round(Math.max(0, Math.min(1, (S.opts[k] || 0) / RANGE[k])) * 100);
        $('seR_' + k).value = v; $('seO_' + k).textContent = v;
    });
    $('seVary').checked = !S.opts.samePerPage;
    $('seFmtJpg').textContent = S.pages.length > 1 ? T('fmt_zip') : 'JPG';
}

/* ─── reading the input ───────────────────────────────────────────────────── */

let pdfjsP = null;
function pdfjs() {
    if (!pdfjsP) {
        pdfjsP = import('/vendor/pdfjs/4/pdf.min.mjs').then((m) => {
            m.GlobalWorkerOptions.workerSrc = '/vendor/pdfjs/4/pdf.worker.min.mjs';
            return m;
        }).catch((e) => { pdfjsP = null; throw e; });
    }
    return pdfjsP;
}
/** EXIF turn applied where the browser takes the option; Safari < 17 throws a
 *  TypeError on it — there the plain decode (which already honours EXIF in
 *  WebKit) is the answer, as in image-compressor's orientedBitmap. */
async function orientedBitmap(src) {
    try { return await createImageBitmap(src, { imageOrientation: 'from-image' }); }
    catch (_) { return createImageBitmap(src); }
}
const isPdf = (f) => !!f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name || ''));

async function readFiles(list) {
    const pages = [];
    for (const f of list) {
        if (pages.length >= MAX_PAGES) break;
        if (isPdf(f)) {
            const lib = await pdfjs();
            let doc;
            try { doc = await lib.getDocument({ data: new Uint8Array(await f.arrayBuffer()), isEvalSupported: false }).promise; }
            catch (e) { throw new Error(/password/i.test(String(e && e.message)) ? 'locked' : 'read'); }
            for (let n = 1; n <= doc.numPages && pages.length < MAX_PAGES; n++) {
                const pg = await doc.getPage(n);
                const vp = pg.getViewport({ scale: 1 });
                pages.push({ kind: 'pdf', doc: doc, n: n, wPt: vp.width, hPt: vp.height });
            }
        } else {
            const bmp = await orientedBitmap(f);
            const wPx = bmp.width, hPx = bmp.height;
            bmp.close && bmp.close();
            // A picture becomes an A4 sheet across its short side — the page a
            // scanner would have put it on.
            const portrait = hPx >= wPx;
            const wPt = portrait ? 595.28 : 841.89;
            pages.push({ kind: 'img', blob: f, wPx: wPx, hPx: hPx, wPt: wPt, hPt: wPt * hPx / wPx });
        }
    }
    if (!pages.length) throw new Error('empty');
    return pages;
}

/** The clean page at `k` px per point (never past an image's own pixels). */
async function cleanPage(p, k) {
    let w = Math.max(1, Math.round(p.wPt * k)), h = Math.max(1, Math.round(p.hPt * k));
    const c = document.createElement('canvas');
    if (p.kind === 'pdf') {
        c.width = w; c.height = h;
        const pg = await p.doc.getPage(p.n);
        const vp = pg.getViewport({ scale: k });
        await pg.render({ canvasContext: c.getContext('2d'), viewport: vp }).promise;
        return c;
    }
    const bmp = await orientedBitmap(p.blob);
    if (w > p.wPx) { w = p.wPx; h = p.hPx; }
    c.width = w; c.height = h;
    const g = c.getContext('2d');
    g.fillStyle = '#fff'; g.fillRect(0, 0, w, h);
    g.imageSmoothingQuality = 'high';
    g.drawImage(bmp, 0, 0, w, h);
    bmp.close && bmp.close();
    return c;
}

/* ─── preview ─────────────────────────────────────────────────────────────── */

let drawT = 0;
function schedulePreview() { clearTimeout(drawT); drawT = setTimeout(preview, 60); }

async function preview() {
    const p = S.pages[S.cur]; if (!p) return;
    const gen = ++S.gen;
    $('seBusy').hidden = false;
    try {
        const k = PREVIEW_EDGE / Math.max(p.wPt, p.hPt);
        let clean = S.clean.get(S.cur);
        if (!clean) { clean = await cleanPage(p, k); S.clean.set(S.cur, clean); }
        if (gen !== S.gen) return;
        const shown = S.compare ? clean : scanPage(clean, clean.width, clean.height, S.opts, pageDraw(S.opts, S.cur, S.seed), clean.width / p.wPt);
        const c = $('seCanvas');
        c.width = shown.width; c.height = shown.height;
        c.getContext('2d').drawImage(shown, 0, 0);
    } catch (e) {
        diag({ issue: 'preview_failed', msg: String((e && e.message) || e).slice(0, 90) });
    } finally {
        if (gen === S.gen) $('seBusy').hidden = true;
    }
    $('sePageOf').textContent = T('page_of').replace('%d', String(S.cur + 1)).replace('%n', String(S.pages.length));
    $('sePrev').disabled = S.cur <= 0;
    $('seNext').disabled = S.cur >= S.pages.length - 1;
}

/* ─── the file ────────────────────────────────────────────────────────────── */

function progress(f, txt) {
    $('seProgWrap').hidden = false;
    $('seProg').style.width = Math.round(Math.max(0, Math.min(1, f)) * 100) + '%';
    if (txt != null) $('seProgTxt').textContent = txt;
}
const progressDone = () => { $('seProgWrap').hidden = true; };
const yieldFrame = () => new Promise((r) => (window.__yieldTick ? window.__yieldTick(0).then(r) : setTimeout(r, 0)));

function fmtSize(b) {
    return b >= 1048576 ? (b / 1048576).toFixed(1) + ' ' + T('mb') : Math.max(1, Math.round(b / 1024)) + ' ' + T('kb');
}

function baseName() {
    const f = S.files[0];
    const raw = f ? String(f.name || '').replace(/\.[^.]+$/, '') : '';
    const ON = window.__outputName;
    const base = (raw || 'document') + ' ' + T('file_suffix');
    return ON && ON.sanitize ? ON.sanitize(base) : base.replace(/[\\/:*?"<>|]+/g, '-');
}
const named = (base, ext) => (window.__outputName && window.__outputName.join ? window.__outputName.join(base, ext) : base + '.' + ext);

/** The cap unit: the document. Several pictures picked together are one. */
function capKeys() {
    const TL = window.__toolLimit;
    if (!TL || !S.files.length) return null;
    const keys = S.files.map((f) => TL.fileKey(f)).filter(Boolean);
    if (!keys.length) return null;
    return keys.length === 1 ? keys : ['set:' + keys.sort().join('|')];
}

async function exportFile() {
    if (S.busy || !S.pages.length) return;
    const TL = window.__toolLimit;
    const run = TL ? TL.begin(SLUG, capKeys()) : { commit() {}, abort() {} };
    if (!run) return;
    S.busy = true;
    $('seGo').disabled = true;
    $('seOutcome').textContent = '';
    const started = performance.now();
    const k = S.dpi / 72;
    const n = S.pages.length;
    try {
        let blob, name;
        if (S.fmt === 'pdf') {
            if (!window.PDFLib) throw new Error('pdf-lib');
            const doc = await window.PDFLib.PDFDocument.create();
            for (let i = 0; i < n; i++) {
                progress(i / n, T('building').replace('%d', String(i + 1)).replace('%n', String(n)));
                await yieldFrame();
                const jpg = await scannedJpeg(i, k);
                const img = await doc.embedJpg(new Uint8Array(await jpg.arrayBuffer()));
                const p = S.pages[i];
                const page = doc.addPage([p.wPt, p.hPt]);
                page.drawImage(img, { x: 0, y: 0, width: p.wPt, height: p.hPt });
            }
            // a scan carries no trace of the program that typed the original
            doc.setProducer(''); doc.setCreator('');
            const bytes = await doc.save();
            blob = new Blob([bytes], { type: 'application/pdf' });
            name = named(baseName(), 'pdf');
            const ok = await window.__downloadFile(blob, name);
            if (ok === false) throw new Error('handled');
        } else {
            const items = [];
            for (let i = 0; i < n; i++) {
                progress(i / n, T('building').replace('%d', String(i + 1)).replace('%n', String(n)));
                await yieldFrame();
                const jpg = await scannedJpeg(i, k);
                items.push({ name: named(baseName() + (n > 1 ? ' ' + String(i + 1).padStart(2, '0') : ''), 'jpg'), blob: jpg });
            }
            if (n === 1) {
                blob = items[0].blob; name = items[0].name;
                const ok = await window.__downloadFile(blob, name);
                if (ok === false) throw new Error('handled');
            } else {
                blob = new Blob(items.map((x) => x.blob));
                await window.__batchDeliver.deliver(items, { zipName: named(baseName(), 'zip'), slug: SLUG, archiveOnly: true });
            }
        }
        run.commit();
        progressDone();
        S.last = { blob: blob, name: name };
        mountNext();
        $('seOutcome').textContent = T('done').replace('%s', fmtSize(blob.size)) + ' · ' + T('done_pages').replace('%d', String(n)) + ' · ' + S.dpi + ' dpi';
        complete({ pages: n, dpi: S.dpi, fmt: S.fmt, preset: S.preset, mode: S.opts.mode, bytes: blob.size, ms: Math.round(performance.now() - started) });
    } catch (e) {
        run.abort();
        progressDone();
        const m = String((e && e.message) || e);
        if (m !== 'handled') {
            toast(T('err_build'), 'error');
            diag({ issue: 'export_failed', fmt: S.fmt, msg: m.slice(0, 90) });
        }
    } finally {
        S.busy = false;
        $('seGo').disabled = false;
    }
}

/** «Что дальше» — mounted at the first file handed over (tool-handoff.js is a
 *  deferred script); JPG/ZIP results get no chips — the targets take PDF. */
let nextMounted = false;
function mountNext() {
    const rail = $('seThen');
    if (!rail || !window.__toolHandoff) return;
    if (!nextMounted) {
        nextMounted = true;
        window.__toolHandoff.mount(rail, () => (S.last && S.last.name ? { blob: S.last.blob, filename: S.last.name } : null));
    } else if (window.__toolHandoff.refresh) window.__toolHandoff.refresh(rail);
}

/** One page: clean at the export size, through the scanner, as JPEG bytes. */
async function scannedJpeg(i, k) {
    const p = S.pages[i];
    const clean = await cleanPage(p, k);
    const out = scanPage(clean, clean.width, clean.height, S.opts, pageDraw(S.opts, i, S.seed), clean.width / p.wPt);
    clean.width = 0; clean.height = 0;
    const blob = await window.__canvasBlob(out, 'image/jpeg', JPEG_Q);
    out.width = 0; out.height = 0;
    return blob;
}

/* ─── wiring ──────────────────────────────────────────────────────────────── */

async function takeFiles(list) {
    if (!list || !list.length) return;
    S.files = Array.prototype.slice.call(list);
    funnel('input', { count: S.files.length, pdf: S.files.filter(isPdf).length });
    $('seBusy').hidden = false;
    $('seWork').hidden = false;
    $('seHint').hidden = true;
    try {
        S.pages = await readFiles(S.files);
    } catch (e) {
        const m = String((e && e.message) || e);
        toast(m === 'locked' ? T('err_locked') : m === 'empty' ? T('err_empty') : T('err_read'), 'error');
        diag({ issue: 'input_failed', msg: m.slice(0, 80) });
        reset();
        return;
    }
    if (S.pages.length >= MAX_PAGES) toast(T('too_many').replace('%d', String(MAX_PAGES)), 'warning');
    S.cur = 0; S.clean.clear();
    S.fmt = 'pdf';
    $('seFile').textContent = S.files.map((f) => f.name).join(', ') + ' · ' + T('pages_n').replace('%d', String(S.pages.length));
    stage('loaded', { pages: S.pages.length });
    syncControls();
    preview();
}

function reset() {
    S.files = []; S.pages = []; S.clean.clear(); S.cur = 0;
    $('seWork').hidden = true;
    $('seHint').hidden = false;
    $('seOutcome').textContent = '';
    try { window.ToolUploader && window.ToolUploader.clear && window.ToolUploader.clear($('seUpload')); } catch (_) {}
}

function onFine() {
    FINE.forEach((k) => {
        const v = +$('seR_' + k).value;
        $('seO_' + k).textContent = v;
        S.opts[k] = (v / 100) * RANGE[k];
    });
    schedulePreview();
}

function init() {
    if (!$('se')) return;
    S.opts = optsFromPreset(S.preset);
    const zone = $('seUpload');
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(zone, (f, d) => takeFiles((d && d.files) || (f ? [f] : [])));
    else zone.addEventListener('tool-uploader:file', (e) => takeFiles((e.detail && (e.detail.files || [e.detail.file])) || []));
    zone.addEventListener('tool-uploader:clear', reset);

    document.querySelectorAll('input[name="sePreset"]').forEach((r) => r.addEventListener('change', () => {
        if (!r.checked) return;
        S.preset = r.value; setPref('preset', S.preset);
        const vary = S.opts.samePerPage;
        S.opts = optsFromPreset(S.preset); S.opts.samePerPage = vary;
        funnel('preset', { p: S.preset });
        syncControls(); schedulePreview();
    }));
    $('seMode').addEventListener('click', (e) => {
        const b = e.target.closest('[data-v]'); if (!b) return;
        S.opts.mode = b.dataset.v; funnel('mode', { m: S.opts.mode });
        syncControls(); schedulePreview();
    });
    $('seDpi').addEventListener('click', (e) => {
        const b = e.target.closest('[data-v]'); if (!b) return;
        S.dpi = +b.dataset.v; setPref('dpi', S.dpi); syncControls();
    });
    $('seFmt').addEventListener('click', (e) => {
        const b = e.target.closest('[data-v]'); if (!b) return;
        S.fmt = b.dataset.v; syncControls();
    });
    FINE.forEach((k) => $('seR_' + k).addEventListener('input', onFine));
    $('seVary').addEventListener('change', () => { S.opts.samePerPage = !$('seVary').checked; schedulePreview(); });
    $('seReseed').addEventListener('click', () => { S.seed = (Math.random() * 4294967296) >>> 0; funnel('reseed', {}); schedulePreview(); });
    $('sePrev').addEventListener('click', () => { if (S.cur > 0) { S.cur--; preview(); } });
    $('seNext').addEventListener('click', () => { if (S.cur < S.pages.length - 1) { S.cur++; preview(); } });
    $('seCompare').addEventListener('click', () => {
        S.compare = !S.compare;
        $('seCompare').setAttribute('aria-pressed', S.compare ? 'true' : 'false');
        $('seCompare').textContent = S.compare ? T('compare_off') : T('compare');
        preview();
    });
    $('seGo').addEventListener('click', exportFile);
    syncControls();
    stage('ready');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
